import ComponentManager from '@/Management/ComponentManager';
import ToolBarLinkViewModel from '@/Components/Links/ToolBarLinkViewModel';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import TypeSelectorWindowViewModelType from '@/ViewModels/Controls/TypeSelectorWindowViewModel';
import TypeSelectorWindowType from '@/Controls/TypeSelectorWindow';
import SearchListViewViewModelType from '@/ViewModels/Views/SearchListViewViewModel';
import * as CoreFrameDTOs from "coreframetypings/dto";
import Utilities from '@/Utility/Utilities';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import Loader from '@/Utility/Loader';
import NavigationService from '@/Management/NavigationService';
import { IRefreshable } from '#/DefaultViewExtensionInterfaces';
import SearchListViewViewFactory from '@/Factories/ViewFactories/SearchListViewViewFactory';
import AssociationHelper from '@/Utility/Association/AssociationHelper';

export default class FindAndConnectLinkViewModel extends ToolBarLinkViewModel {

    protected dataSource: IRefreshable;
    protected listView: KnockoutObservable<CoreFrameDTOs.IListViewDTO>;
    protected contextParameters: KnockoutObservable<any>;
    protected contextPdoId: KnockoutObservable<any>;
    protected contextTypeName: KnockoutObservable<string>;
    protected contextRelationName: KnockoutObservable<string>;
    protected typeName: KnockoutObservable<string>;
    protected detailViewItem: CoreFrameDTOs.IDVGItemDTO;
    protected showTypeSelector: boolean;
    protected searchViewFilter: string;
    private typeSelectorWindowViewModel: TypeSelectorWindowViewModelType;
    private viewFactorySearchListView: SearchListViewViewFactory;

    public constructor(protected params: any) {
        super($.extend(params,
            {
                ergName: 'FIND_AND_CONNECT',
                tooltipErgName: 'FIND_AND_CONNECT_TOOLTIP',
                imageSource: GlobalConfiguration.inlineImages.TOOLBAR_SELECT,
                locator: 'select'
            }));

        this.contextPdoId = this.observableOrDefault(params.contextPdoId, this.context.genericObject.pdoId);
        this.contextTypeName = this.observableOrDefault(params.contextTypeName, this.context.genericObject.pdTypeName);

        this.setRequiredObservables();
        this.typeName = this.defineComputed(() => {
            let paramTypeName = ko.unwrap(params.pdTypeName)

            if (!Utilities.isNullOrUndefined(paramTypeName))
                return paramTypeName;

            return this.listView().typeName;
        });

        if (params.contextRelationName)
            this.contextRelationName = this.observableOrDefault(params.contextRelationName);
        else
            this.contextRelationName = this.defineComputed(() => this.listView().relationName);

        this.dataSource = this.paramOrDefault(params.dataSource, null);
    }

    /**
     * @override 
     */
    protected createIsVisibleObservable(): KnockoutObservable<boolean> {
        if (!Utilities.isNullOrUndefined(this.params.isVisible)) {
            return this.observableOrDefault(this.params.isVisible);
        }

        this.setRequiredObservables();

        return this.createWritableComputed(() => !this.params.context?.isSummaryViewMode() && this.listView().showButtonSelect);
    }

    /**
     * @override 
     */
    public onClick(openInNewWindow?: boolean, event?: JQuery.Event): void {
        const typeSelectorWindowViewModel = ComponentManager.getViewModel<typeof TypeSelectorWindowViewModelType>('TypeSelectorWindowViewModel');
        const TypeSelectorWindow = ComponentManager.getControl<typeof TypeSelectorWindowType>('TypeSelectorWindow');

        if (this.listView().typeName != this.typeName()) {
            RADEnvironmentProxy.getInstance().getListViewByName({
                listViewName: this.typeName() + '_LV'
            }).then((listView: CoreFrameDTOs.IListViewDTO) => {
                this.openSearchListView(listView);
            });
        }
        else if (this.showTypeSelector && this.listView().types.length > 1) {
            if (this.typeSelectorWindowViewModel == null) {
                this.typeSelectorWindowViewModel = typeSelectorWindowViewModel.createSubTypeSelectorWindow(
                    this.listView().ergName,
                    this.listView().types,
                    (typeName: string) => {
                        RADEnvironmentProxy.getInstance().getListViewByName({
                            listViewName: typeName + '_LV'
                        }).then((listView: CoreFrameDTOs.IListViewDTO) => {
                            listView = this.copyListViewProperties(this.listView(), listView);
                            this.openSearchListView(listView);
                        });
                    },
                    this.parentLocator,
                    () => {
                        if (this.typeSelectorWindowViewModel && this.typeSelectorWindowViewModel.window) {
                            const $context = this.typeSelectorWindowViewModel.window.content();
                            if ($context) {
                                $context.closest('.k-window').remove();
                            }
                        }
                        this.typeSelectorWindowViewModel = null;
                    }
                );

                const selectBaseObjectWindow = new TypeSelectorWindow(this.typeSelectorWindowViewModel);
                selectBaseObjectWindow.getTuple();
            }
        }
        else {
            this.openSearchListView(this.listView());
        }
    }

    /**
     * Öffnet die Suchliste
     * @param listView
     */
    protected openSearchListView(listView: CoreFrameDTOs.IListViewDTO): void {
        const SearchListViewViewModel = ComponentManager.getViewModel<typeof SearchListViewViewModelType>('SearchListViewViewModel');
        if (this.viewFactorySearchListView == null) {
            this.viewFactorySearchListView = NavigationService.getInstance().loadViewFactoryIsolated('SearchListView', {
                viewModel: new SearchListViewViewModel({
                    parentLocator: this.toolBar?.parentLocator(),
                    locator: 'search-view',
                    contextPdoId: this.contextPdoId(),
                    listView: listView,
                    allowMultiSelect: true,
                    serializedFilter: this.searchViewFilter,
                    callback: (toPdoIds: Array<string>) => {
                        const pdoId = this.contextPdoId();
                        const associationName = this.contextRelationName();
                        const pdTypeName = this.typeName();
                        const nestedContextName = this.context.getNestedContextPath();
                        this.connect(pdoId, associationName, pdTypeName, toPdoIds, nestedContextName);
                    },
                    onClose: () => {
                        this.viewFactorySearchListView.viewModel.dataGrid.filter.forceFilterExpressionObjectCalculation();
                        this.searchViewFilter = this.viewFactorySearchListView.viewModel.dataGrid.filter.serializedFilter();
                        this.hasFocus(true);
                        NavigationService.getInstance().removeViewFactoryIsolated(this.viewFactorySearchListView);
                        this.viewFactorySearchListView = null;
                    }
                })
            }, true);
        }
    }

    /**
     * Die Ausführung des eigentlich Verbindens
     * @param pdoId
     * @param associationName
     * @param topdTypeName
     * @param toPdoIds
     * @param nestedContextName
     */
    protected connect(pdoId: string, associationName: string, topdTypeName: string, toPdoIds: Array<string>, nestedContextName: string): void {
        $.when(this.context.notifyBeforeUpdatedAssociation(associationName, 'connect', { toPdoIds: toPdoIds }))
            .then((doUpdateAssociation) => {
                if (doUpdateAssociation == null || doUpdateAssociation === true) {
                    Loader.getInstance().show();
                    this.callConnectOperation(pdoId, associationName, topdTypeName, toPdoIds, nestedContextName)
                        .then((changeObject: CoreFrameDTOs.IChangeObjectDTO) => {
                            this.connectCallback(changeObject);
                        }).always(() => {
                            Loader.getInstance().hide();
                        });
                }
            });
    }

    /**
     * Ruft die Methode zum Verbinden auf
     * @param pdoId
     * @param associationName
     * @param topdTypeName
     * @param toPdoIds
     * @param nestedContextName
     */
    protected callConnectOperation(pdoId: string, associationName: string, topdTypeName: string, toPdoIds: Array<string>, nestedContextName: string): JQueryPromise<any> {
        return RADEnvironmentProxy.getInstance().connectMany({
            pdoID: pdoId,
            assoziationName: associationName,
            topdTypeName: topdTypeName,
            toPDOIDs: toPdoIds,
            nestedContextName: nestedContextName
        }).promise();
    }

    /**
     * Das Callback, welches nach dem Connect ausgeführt wird
     * @param callbackResult
     */
    protected connectCallback(callbackResult: any): void {
        // Auch im Fehlerfall kann sich die Assoziation geändert haben, da alle Objekte bis zum Fehler verbunden werden
        // Nachfolgende Elemente (ab Index, wo der Fehler auftritt) werden nicht mehr verbunden
        this.validateStatus(callbackResult as CoreFrameDTOs.IChangeObjectDTO);
        // Alle geänderten Assoziationen werden geladen, auch wenn sie Lazy sind
        AssociationHelper.activateDataLoading(this.context, callbackResult.touchedMembers, this.listView().relationName);
        this.dataSource?.refresh();
        this.context.genericObject.isModified(true);
        this.context.notifyUpdatedAssociation(this.listView().relationName);
        this.context.notifyFactoryRefreshed(this.detailViewItem);
    }

    /**
     * Da bereits im Konstruktor der Oberklassen Methoden aufgerufen werden, die auf Member dieser Klasse zugreifen
     * muss dies in eine Methode ausgelagert werden
     */
    protected setRequiredObservables(): void {
        if (Utilities.isNullOrUndefined(this.listView)) {
            this.listView = this.observableOrDefault(this.params.listView);
        }

        if (Utilities.isNullOrUndefined(this.showTypeSelector)) {
            this.showTypeSelector = this.paramOrDefault(this.params.showTypeSelector, true);
        }

        if (Utilities.isNullOrUndefined(this.searchViewFilter)) {
            this.searchViewFilter = '';
        }

        if (Utilities.isNullOrUndefined(this.detailViewItem)) {
            this.detailViewItem = this.paramOrDefault(this.params.detailViewItem, null);
        }
    }

    private copyListViewProperties(source: CoreFrameDTOs.IListViewDTO, target: CoreFrameDTOs.IListViewDTO): CoreFrameDTOs.IListViewDTO {
        target.relationName = source.relationName;

        return target;
    }
}

ComponentManager.setViewModel('FindAndConnectLink', FindAndConnectLinkViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');